import QueryString from 'query-string';
import { TrackJS } from 'trackjs';

import jsonp from '@module/http/axios/jsonp';

import Advert from './advert';
import OpenXConfig from './config';
import OpenXEnvironment from './environment';
import OpenXUnits from './units';

export default class OpenX {
  defaultRegion = false;
  currentRegion = false;
  currentAdvert = false;
  currentPosition = 0; // getting an advert will start with +1 so first is 1

  static configure(opts = {}) {
    OpenXConfig.set(opts);
  }

  static setLoggedIn(state) {
    OpenXEnvironment.setLoggedIn(state);
  }

  constructor(options = { noAds: false }) {
    this.determineEndpoint(options.noAds);

    // Determine client parameters
    const clientParams = QueryString.parse(location.search);
    let { country, continent } = OpenXConfig.get();

    if ('country' in clientParams) {
      country = clientParams['country'];
    }

    if ('continent' in clientParams) {
      continent = clientParams['continent'];
    }

    // Where is the user located?
    this.currentRegion = OpenXUnits.getRegion({
      country,
      continent,
    });

    this.defaultRegion = OpenXUnits.getRegion({
      country: '*',
    });
  }

  determineEndpoint(useFallback = false) {
    const { openx, fallback } = OpenXConfig.get('endpoints');

    // Using fallback (probably ads blocked or asset failed to load)
    this.endpoint = useFallback ? fallback : openx;
  }

  getDuration() {
    // 0 doesn't have a timer usually, as it's not really a valid value.
    // however it can have this value when the first advert failed, so for
    // that scenario we'll default it to position 1 to get the correct timer.
    const position = Math.max(1, this.currentPosition);
    let timer = this.currentRegion.timers[position];

    // Try the default region timer settings
    if (!timer) {
      timer = this.defaultRegion.timers[position];
    }

    // Still no timer? Try the default region default timer
    if (!timer) {
      timer = this.defaultRegion.timers['*'];
    }

    // Return the duration for this advert
    return timer;
  }

  async getAdvert() {
    // Assume we are in the next position
    const nextPosition = this.currentPosition + 1;

    // Obtain the auid for this region and position
    let advertUnitId = OpenXUnits.getPosition({
      region: this.currentRegion,
      position: nextPosition,
    });

    // Allow overwriting of adunitid
    const clientParams = QueryString.parse(location.search);
    if ('mm1x' in clientParams) {
      const mm1x = parseInt(clientParams['mm1x'], 10);
      if (!isNaN(mm1x)) {
        advertUnitId = mm1x;
      }
    }

    // Build the request params for the next advert
    const params = {
      auid: advertUnitId,
      ...OpenXEnvironment.getParams(),
    };

    try {
      const response = await jsonp({
        url: this.endpoint,
        timeout: OpenXConfig.get('timeout'),
        params,
      });
      if (!response.body) {
        throw 'MissingBody';
      }
      if (!response.body.ads) {
        throw 'MissingAdvert';
      }
      if (!response.body.ads.count) {
        throw 'NoAdverts';
      }

      // Get the first ad in this response
      const advert = response.body.ads.ad[0];
      advert.position = nextPosition;

      // Attempt to parse the ad data and update position
      const ad = new Advert(advert);
      this.currentPosition = nextPosition;

      // Now we will return the advert
      return ad;
    } catch (e) {
      // We are measuring failed adverts
      if (e && e.status === 0) {
        return false;
      }

      TrackJS.addMetadata('advert_unit', advertUnitId);
      TrackJS.track(e);
    }

    // Something went wrong, return false (will go to default)
    return false;
  }
}
